import { newRelic } from '~/utils/newRelic/newRelic';

import {
  type ScanUiCloseTarget,
  type ScanUiCloseTargetRegistry,
} from '../types/scanUiCloseTarget.type';

/**
 * Tracks the close callbacks of sub-screens open on the order details page.
 * Each sub-screen's open state lives in local component state, so it
 * publishes its close callback here while open, and the scan action that
 * reopens the purchase flow calls `closeAll` to dismiss them first.
 */
export const createScanUiCloseTargetRegistry = (): ScanUiCloseTargetRegistry => {
  const targets = new Map<symbol, ScanUiCloseTarget>();

  return {
    closeAll: () => {
      // A throwing close() must not strand the sub-screens that would close after it.
      targets.forEach((target) => {
        try {
          target.close();
        } catch (error) {
          newRelic.noticeError('Failed to close sub-screen on scan', { error });
        }
      });
    },
    register: (target) => {
      const token = Symbol('scanUiCloseTarget');
      targets.set(token, target);

      return {
        release: () => {
          targets.delete(token);
        },
        update: (nextTarget) => {
          if (targets.has(token)) targets.set(token, nextTarget);
        },
      };
    },
    reset: () => targets.clear(),
  };
};
